import { useState, useCallback } from "react";
import { GetZohoInvoiceDetailsUseCase } from "../../../../shared/domain/use-cases/GetZohoInvoiceDetailsUseCase";
import { InvoiceRepositoryImpl } from "../../../../shared/data/repositories/InvoiceRepositoryImpl";
import { ZohoInvoiceDetailsEntity } from "../../../../shared/domain/entities/InvoiceListItemEntity";

export interface UseZohoInvoiceDetailsReturn {
  invoiceDetails: ZohoInvoiceDetailsEntity | null;
  loading: boolean;
  error: string | null;
  fetchInvoiceDetails: (zohoInvoiceId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

// Create repository and use case instances (singleton pattern)
const invoiceRepository = new InvoiceRepositoryImpl();
const getZohoInvoiceDetailsUseCase = new GetZohoInvoiceDetailsUseCase(
  invoiceRepository
);

/**
 * Hook for fetching Zoho invoice details
 * Used when an invoice is selected to populate debit note line items
 */
export const useZohoInvoiceDetails = (): UseZohoInvoiceDetailsReturn => {
  const [invoiceDetails, setInvoiceDetails] =
    useState<ZohoInvoiceDetailsEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch Zoho invoice details by Zoho invoice ID
   */
  const fetchInvoiceDetails = useCallback(
    async (zohoInvoiceId: string): Promise<void> => {
      if (!zohoInvoiceId || zohoInvoiceId.trim() === "") {
        setError("Zoho invoice ID is required");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Use the use case to fetch invoice details
        const result = await getZohoInvoiceDetailsUseCase.execute(
          zohoInvoiceId.trim()
        );

        setInvoiceDetails(result);
      } catch (error: any) {
        const errorMessage = error.message || "Failed to fetch invoice details";
        setError(errorMessage);
        console.error("Failed to fetch Zoho invoice details:", error);
        setInvoiceDetails(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback((): void => {
    setInvoiceDetails(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    invoiceDetails,
    loading,
    error,
    fetchInvoiceDetails,
    clearError,
    reset,
  };
};
